import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { ClipboardList, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const ACTION_LABELS = {
  update_user_tier: 'Updated user tier',
  provision_subscription: 'Provisioned subscription',
  feature_flag_enabled: 'Enabled feature flag',
  feature_flag_disabled: 'Disabled feature flag',
  export_users: 'Exported users CSV',
};

export default function AdminAuditLogPage() {
  const { isDark, colors } = useAdminTheme();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-log?page=${p}&limit=30`, { withCredentials: true });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPage(p);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const totalPages = Math.ceil(total / 30);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-audit-log-page">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Audit Log</h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Track all admin actions</p>
        </div>

        <div className="rounded-2xl overflow-hidden" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
              <p style={{ color: colors.textMuted }}>No audit log entries yet</p>
            </div>
          ) : (
            <table className="w-full text-sm" style={{ color: colors.text }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: colors.textMuted }}>Time</th>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: colors.textMuted }}>Admin</th>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: colors.textMuted }}>Action</th>
                  <th className="text-left px-4 py-3 font-medium" style={{ color: colors.textMuted }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.cardBorder}` }} data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-3 text-xs" style={{ color: colors.textMuted }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium">{log.admin_name}</td>
                    <td className="px-4 py-3 text-xs">{ACTION_LABELS[log.action] || log.action}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: colors.textMuted }}>
                      {log.target_type && <span>{log.target_type}: {log.target_id}</span>}
                      {log.details && Object.keys(log.details).length > 0 && (
                        <span className="ml-2">({Object.entries(log.details).map(([k,v]) => `${k}=${v}`).join(', ')})</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchLogs(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm" style={{ color: colors.textMuted }}>Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
